import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Folder, ArrowLeft, LogOut, Bell, X, Download, ExternalLink, ChevronRight, Coffee,
  FileText, Upload, FolderPlus, File as FileIcon, Trash2, ShieldCheck, Globe, Calculator,
  CreditCard, Loader2,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { CafeChat } from "@/components/CafeChat";
import { CostEstimator } from "@/components/CostEstimator";
import { CessionSimulator } from "@/components/CessionSimulator";
import { FileThumb, FilePreviewModal, fileUrl, driveFileUrl, isImage, isPdf } from "@/components/FilePreviewModal";
import { useAuth } from "@/context/AuthContext";

const DOSSIERS = [
  { k: "actualite", label: "Actualité" },
  { k: "exercice", label: "Exercice en cours" },
  { k: "exercice_n1", label: "Exercice N-1" },
  { k: "archives", label: "Archives" },
  { k: "comptabilite", label: "Comptabilité" },
  { k: "facturation", label: "Factures" },
  { k: "a_payer", label: "À payer" },
];

const BOTTOM_IMG =
  "https://images.squarespace-cdn.com/content/v1/65ea2001529ccd1a677ed52f/da9502a2-8fab-4928-8fba-39a0bb756a60/Le+30+Du+mois.png";

const token = () => localStorage.getItem("l30m_token");
const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
const unpaidOnly = (items) => items.filter((i) => !i.paid).length;

function humanSize(b) {
  if (!b) return "";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("home"); // "home" | category key
  const [bgId, setBgId] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [fiches, setFiches] = useState(null); // null closed, array open
  const [preview, setPreview] = useState(null);
  const [driveItems, setDriveItems] = useState([]);
  const [drivePath, setDrivePath] = useState([]); // [{id,name}]
  const [driveLoading, setDriveLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [visuelUrl, setVisuelUrl] = useState(null);
  const [fichePdf, setFichePdf] = useState(null);
  const [driveUrl, setDriveUrl] = useState("");
  const [website, setWebsite] = useState("");
  const [cafeOpen, setCafeOpen] = useState(false);
  const [cafeUnread, setCafeUnread] = useState(0);
  const [showEstimator, setShowEstimator] = useState(false);
  const [showCession, setShowCession] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [payingId, setPayingId] = useState(null);
  const [payEnabled, setPayEnabled] = useState(false);

  const payInvoice = async (it) => {
    setPayingId(it.id);
    try {
      const r = await api.post(`/portal/invoices/${it.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url; // redirection vers la page de paiement sécurisée Stripe
    } catch (e) {
      toast.error(e.response?.data?.detail || "Paiement indisponible pour le moment.");
      setPayingId(null);
    }
  };

  // Pastille de non-lus du Café du 30 (salon fermé : vérification toutes les 30 s)
  useEffect(() => {
    if (cafeOpen) { setCafeUnread(0); return; }
    const fetchUnread = () => api.get("/chat/messages").then((r) => setCafeUnread(r.data.unread)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [cafeOpen]);
  const fileInput = useRef(null);
  const folderInput = useRef(null);
  const [portalFolder, setPortalFolder] = useState(null); // dossier de dépôt ouvert {id, name} ou null
  const [uploadLabel, setUploadLabel] = useState(null);

  const loadNotifs = useCallback(() => {
    api.get("/portal/notifications").then((r) => {
      setNotifs(r.data.notifications); setUnread(r.data.unread);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/public/config`).then((r) => r.json()).then((d) => setPayEnabled(!!d.payments_enabled)).catch(() => {});
    api.get("/portal/settings").then((r) => {
      setBgId(r.data.background_file_id);
      setDriveUrl(r.data.drive_url || "");
      setWebsite(r.data.website || "");
    }).catch(() => {});
    loadNotifs();
    api.get("/portal/invoices").then((r) => setInvoiceCount(unpaidOnly(r.data.items || []))).catch(() => {});
    const code = (user?.code || "").toLowerCase();
    if (!code) return;
    api.get("/portal/drive?category=visuels").then((r) => {
      const imgs = (r.data.items || []).filter((it) => !it.isFolder && isImage(it.mimeType));
      const match = imgs.find((it) => it.name.toLowerCase().includes(code));
      if (match) setVisuelUrl(driveFileUrl(match.id));
    }).catch(() => {});
    api.get("/portal/drive?category=fiches").then((r) => {
      const pdfs = (r.data.items || []).filter((it) => !it.isFolder && (isPdf(it.mimeType) || it.name.toLowerCase().endsWith(".pdf")));
      const match = pdfs.find((it) => it.name.toLowerCase().includes(code));
      if (match) setFichePdf(match);
    }).catch(() => {});
  }, [loadNotifs, user?.code]);

  const openFolder = (k) => {
    setView(k); setLoading(true); setDrivePath([]); setPortalFolder(null);
    if (k === "a_payer") {
      api.get("/portal/invoices?consult=1").then((r) => {
        setInvoices(r.data.items || []);
        setInvoiceCount(unpaidOnly(r.data.items || []));
      }).catch(() => toast.error("Chargement impossible.")).finally(() => setLoading(false));
      return;
    }
    api.get(`/portal/files?category=${k}`).then((r) => setFiles(r.data.files))
      .catch(() => toast.error("Chargement impossible.")).finally(() => setLoading(false));
    loadDrive({ category: k });
  };

  const loadDrive = ({ category, folderId }) => {
    setDriveLoading(true);
    const q = folderId ? `folder_id=${folderId}` : `category=${category}`;
    api.get(`/portal/drive?${q}`).then((r) => setDriveItems(r.data.items || []))
      .catch(() => setDriveItems([])).finally(() => setDriveLoading(false));
  };
  const openDriveFolder = (item) => {
    setDrivePath((p) => [...p, { id: item.id, name: item.name }]);
    loadDrive({ folderId: item.id });
  };
  const driveJumpTo = (index) => {
    if (index < 0) { setDrivePath([]); loadDrive({ category: view }); return; }
    const np = drivePath.slice(0, index + 1);
    setDrivePath(np);
    loadDrive({ folderId: np[np.length - 1].id });
  };

  const reloadFolder = () => openFolder(view);

  const handleLogout = () => { logout(); navigate("/", { replace: true }); };

  const markRead = async () => { await api.post("/portal/notifications/read"); setUnread(0); loadNotifs(); };

  const openFiches = async () => {
    try {
      const r = await api.get("/portal/files?category=fiches");
      setFiches(r.data.files);
    } catch { toast.error("Impossible de charger vos fiches."); }
  };

  const uploadFiles = async (list) => {
    if (!list || !list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "actualite");
        await api.post("/portal/upload", fd);
      }
      toast.success(`${list.length} dépôt(s) ajouté(s). Notification envoyée.`);
      reloadFolder(); loadNotifs();
    } catch { toast.error("Échec de l'envoi."); } finally { setUploading(false); }
  };

  const createFolder = async () => {
    folderInput.current?.click();
  };

  // Dépôt d'un dossier local complet : crée le dossier dans l'application puis envoie tout son contenu.
  const uploadFolder = async (list) => {
    if (!list || !list.length) return;
    const folderName = (list[0].webkitRelativePath || "").split("/")[0] || "Dossier";
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("is_folder", "true"); fd.append("folder_name", folderName); fd.append("category", "actualite");
      const r = await api.post("/portal/upload", fd);
      const folderId = r.data.id;
      let done = 0;
      for (const f of list) {
        done += 1;
        setUploadLabel(`Envoi ${done}/${list.length}…`);
        const fdd = new FormData();
        fdd.append("file", f, f.name);
        fdd.append("category", "actualite");
        fdd.append("folder_id", folderId);
        fdd.append("notify", "false"); // une seule notification : celle du dossier
        await api.post("/portal/upload", fdd);
      }
      toast.success(`Dossier « ${folderName} » déposé (${list.length} fichier${list.length > 1 ? "s" : ""}). Notification envoyée.`);
      reloadFolder(); loadNotifs();
    } catch { toast.error("Échec de l'envoi du dossier."); reloadFolder(); } finally { setUploading(false); setUploadLabel(null); }
  };

  const removeFile = async (id) => {
    await api.delete(`/portal/files/${id}`);
    setFiles((p) => p.filter((f) => f.id !== id && f.folder_id !== id)); // dossier → son contenu part aussi
    toast.success("Élément supprimé.");
  };

  const openDrive = () => {
    if (!driveUrl) {
      toast.error("Lien Drive non configuré — contactez Le 30 du mois.");
      return;
    }
    window.open(driveUrl, "_blank", "noopener,noreferrer");
  };

  const bgUrl = bgId ? `${API}/portal/files/${bgId}/download?auth=${token()}` : null;
  const clientName = user?.name || user?.code || "Client";

  return (
    <div className="min-h-screen bg-ink text-[#F3F4F6] font-body">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-white/[0.04] backdrop-blur-2xl">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif text-lg tracking-tight">LE 30 <span className="text-gold italic">DU MOIS</span></span>
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A0B2CE] border-l border-white/15 pl-4" data-testid="client-name">{clientName}</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <div className="relative">
              <button onClick={() => { setPanelOpen((v) => !v); if (!panelOpen && unread) markRead(); }} data-testid="notif-bell" className="relative w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-gold transition-colors">
                <Bell className="w-4 h-4 text-gold" />
                {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-gold text-ink text-[10px] font-mono flex items-center justify-center">{unread}</span>}
              </button>
              <AnimatePresence>
                {panelOpen && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="absolute right-0 mt-3 w-80 bg-[#0B2A52] border border-white/10 rounded-lg p-4 z-50 shadow-2xl" data-testid="notif-panel">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Notifications</p>
                      <button onClick={() => setPanelOpen(false)}><X className="w-4 h-4 text-[#A0B2CE]" /></button>
                    </div>
                    {notifs.length === 0 ? <p className="text-sm text-[#A0B2CE]">Aucune notification.</p> : (
                      <div className="space-y-3 max-h-72 overflow-auto">
                        {notifs.map((n) => (
                          <div key={n.id} className="border-b border-white/5 pb-3">
                            <p className="text-sm">{n.message}</p>
                            <p className="text-[11px] text-[#A0B2CE] mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {website && (
              <a href={website} target="_blank" rel="noopener noreferrer" data-testid="my-site-btn" title="Ouvrir votre site internet dans un nouvel onglet" className="hidden sm:inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors">
                <Globe className="w-4 h-4" /> Votre site
              </a>
            )}
            <button onClick={openDrive} data-testid="shortcut-btn" title="Ouvrir votre dossier Drive dans un nouvel onglet" className="hidden sm:inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors">
              <ExternalLink className="w-4 h-4" /> Mon Drive
            </button>
            <button onClick={() => setShowEstimator(true)} data-testid="estimator-open-btn" title="Estimer le coût total d'une paie (cachets + charges) avant de nous la confier"
              className="hidden sm:inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors">
              <Calculator className="w-4 h-4" /> Estimateur
            </button>
            <button onClick={() => setShowCession(true)} data-testid="cession-open-btn" title="Calculer un prix de cession pour 1 date, hors V.H.R., hors droits et hors rémunérations producteur et diffuseur"
              className="hidden sm:inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors">
              <Calculator className="w-4 h-4" /> Cession
            </button>
            <button onClick={() => setCafeOpen((v) => !v)} data-testid="cafe-open-btn" title="Le Café du 30 — salon commun"
              className="relative hidden sm:inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors">
              <Coffee className="w-4 h-4" /> Le Café
              {!cafeOpen && cafeUnread > 0 && (
                <span data-testid="cafe-unread-badge" className="absolute -top-1.5 -right-1.5 bg-gold text-ink text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{cafeUnread}</span>
              )}
            </button>
            <button onClick={handleLogout} data-testid="logout-btn" title="Déconnexion" className="text-[#A0B2CE] hover:text-red-400 transition-colors"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      {cafeOpen ? (
        <div className="max-w-[1100px] mx-auto w-full px-4 md:px-8 py-8 h-[calc(100vh-110px)]" data-testid="cafe-section">
          <CafeChat onClose={() => setCafeOpen(false)} />
        </div>
      ) : view === "home" ? (
        <>
          {/* Hero */}
          <section className="px-4 md:px-8 pt-6">
            <div className="relative max-w-[1500px] mx-auto rounded-b-[2rem] overflow-hidden min-h-[46vh] flex items-center justify-center" data-testid="client-hero">
              <div className="absolute inset-0">
                {bgUrl ? <img src={bgUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[#0B2A52] to-ink" />}
                <div className="absolute inset-0 bg-ink/70" />
              </div>
              <div className="relative z-10 text-center px-6 py-20">
                {fichePdf ? (
                  <button onClick={() => setPreview({ id: fichePdf.id, name: fichePdf.name, mimeType: fichePdf.mimeType, driveUrl: driveFileUrl(fichePdf.id) })} data-testid="fiches-link" className="font-mono text-xs tracking-[0.2em] uppercase text-gold underline underline-offset-4 hover:text-white transition-colors mb-6">
                    Rappels de vos informations et identifiants
                  </button>
                ) : (
                  <button onClick={openFiches} data-testid="fiches-link" className="font-mono text-xs tracking-[0.2em] uppercase text-gold underline underline-offset-4 hover:text-white transition-colors mb-6">
                    Rappel données personnelles
                  </button>
                )}
                <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-tight">Bienvenue sur votre<br />espace <span className="italic text-gold">dédié sécurisé</span></h1>
              </div>
            </div>
          </section>

          {/* Accès direct */}
          <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 md:py-24">
            <h2 className="text-center font-mono text-sm tracking-[0.3em] uppercase text-gold mb-14" data-testid="acces-direct-title">/ Accès direct</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {DOSSIERS.map((d, i) => (
                <motion.div key={d.k} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className={`group border rounded-2xl p-7 flex flex-col items-center text-center bg-white/[0.02] hover:bg-white/[0.05] transition-colors ${d.k === "a_payer" ? "border-[#C0392B]/50 hover:border-[#E74C3C]/70" : "border-white/10 hover:border-gold/40"}`} data-testid={`card-${d.k}`}>
                  <img src="/logo-30.png" alt="" className="w-16 h-16 rounded-lg mb-5 border border-gold/30 group-hover:border-gold/60 transition-colors" />
                  <p className="font-serif text-xl mb-2">{d.label}</p>
                  {d.k === "a_payer" && invoiceCount > 0 && (
                    <span data-testid="invoice-count-badge" className="mb-2 inline-flex items-center rounded-full bg-[#C0392B] text-white font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1">
                      {invoiceCount} à régler
                    </span>
                  )}
                  <div className="mb-4" />
                  <button onClick={() => openFolder(d.k)} data-testid={`acces-${d.k}`}
                    className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${d.k === "a_payer" ? "bg-[#C0392B] text-white hover:bg-[#A93226]" : "bg-gold text-ink hover:bg-[#B5952F]"}`}>
                    Accès
                  </button>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Bottom image band */}
          <div className="relative h-56 md:h-80 overflow-hidden border-t border-white/10" data-testid="bottom-band">
            <img src={visuelUrl || BOTTOM_IMG} alt="" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-ink/40" />
          </div>
        </>
      ) : (
        /* Folder view */
        <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-10" data-testid="folder-view">
          <button onClick={() => setView("home")} data-testid="back-home-btn" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>
          <div className="flex items-center gap-4 mb-10">
            <Folder className="w-8 h-8 text-gold" strokeWidth={1.3} />
            <h1 className="font-serif text-4xl">{DOSSIERS.find((d) => d.k === view)?.label}</h1>
            {view !== "a_payer" && (
              <a href={`${API}/portal/folders/${view}/download-zip?auth=${token()}`} data-testid="download-folder-zip"
                title="Télécharger tout le dossier en une archive ZIP"
                className="ml-auto inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors">
                <Download className="w-4 h-4" /> Tout télécharger (.zip)
              </a>
            )}
          </div>

          {view === "a_payer" && (
            <div data-testid="invoices-section">
              <p className="text-sm text-[#A0B2CE] mb-8 max-w-2xl">
                {payEnabled
                  ? "Vos factures en attente de règlement, déposées par Le 30 du mois. Celles affichant un montant peuvent être réglées en ligne par carte bancaire. Une fois réglées, elles sont archivées dans l'onglet « Factures »."
                  : "Vos factures en attente de règlement, déposées par Le 30 du mois. Une fois réglées, elles sont archivées dans l'onglet « Factures »."}
              </p>
              {loading ? (
                <p className="font-mono text-xs tracking-[0.3em] text-gold animate-pulse">CHARGEMENT…</p>
              ) : invoices.length === 0 ? (
                <div className="border border-white/10 rounded-xl p-14 text-center" data-testid="invoices-empty">
                  <p className="text-[#A0B2CE]">Aucune facture en attente de règlement.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="invoices-list">
                  {invoices.map((it) => (
                    <div key={it.id} className={`group border rounded-lg p-5 flex flex-wrap items-center gap-x-4 gap-y-3 transition-colors ${it.paid ? "border-emerald-500/40 bg-emerald-500/[0.04]" : "border-[#C0392B]/40 bg-white/[0.03] hover:bg-white/[0.06]"}`} data-testid={`invoice-${it.id}`}>
                      <div className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${it.paid ? "bg-emerald-500/15" : "bg-[#C0392B]/15"}`}>
                        <FileText className={`w-5 h-5 ${it.paid ? "text-emerald-400" : "text-[#E74C3C]"}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{it.name}</p>
                        <p className="text-[11px] text-[#A0B2CE] font-mono">
                          {payEnabled && it.amount ? <span className="text-gold">{fmtEur(it.amount)}</span> : (it.size ? humanSize(it.size) : "Facture PDF")}
                        </p>
                      </div>
                      {payEnabled && (it.paid ? (
                        <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-500/50 text-emerald-400 font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1.5" data-testid={`invoice-paid-${it.id}`}>
                          <span className="sm:hidden">Payée</span>
                          <span className="hidden sm:inline">Payée{it.paid_at ? ` le ${new Date(it.paid_at).toLocaleDateString("fr-FR")}` : ""}</span>
                        </span>
                      ) : it.amount ? (
                        <button onClick={() => payInvoice(it)} disabled={payingId === it.id} data-testid={`invoice-pay-${it.id}`}
                          title="Régler en ligne par carte bancaire (paiement sécurisé)"
                          className="shrink-0 inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-emerald-500 transition-colors disabled:opacity-50">
                          {payingId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                          Payer
                        </button>
                      ) : (
                        <span className="shrink-0 text-[10px] font-mono uppercase tracking-[0.1em] text-[#A0B2CE]" data-testid={`invoice-noamount-${it.id}`}>Montant à venir</span>
                      ))}
                      <a href={`${API}/portal/invoices/${it.id}/download?auth=${token()}`} target="_blank" rel="noopener noreferrer" title="Consulter la facture" data-testid={`invoice-download-${it.id}`}
                        className="w-9 h-9 rounded-full border border-[#C0392B]/50 text-[#E74C3C] flex items-center justify-center hover:bg-[#C0392B] hover:text-white transition-colors shrink-0">
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {view === "actualite" && (
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
              className="border border-dashed border-white/20 rounded-xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4 bg-white/[0.02]" data-testid="upload-zone">
              <div className="flex items-center gap-4">
                <Upload className="w-6 h-6 text-gold" strokeWidth={1.3} />
                <div>
                  <p className="font-serif text-lg">Déposer un fichier ou un dossier</p>
                  <p className="text-xs text-[#A0B2CE]">« Dossier » ouvre un choix de dossier sur votre ordinateur — tout son contenu est envoyé. Chaque dépôt informe Le 30 du mois par notification, est copié dans le dossier « Dépôts » de votre Drive (bouton « Mon Drive ») et reste visible ici 5 jours. Vous pouvez aussi glisser vos documents directement dans « Dépôts ».</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="upload-file-btn" className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                  <FileIcon className="w-4 h-4" /> {uploading ? (uploadLabel || "Envoi…") : "Fichier"}
                </button>
                <button onClick={createFolder} disabled={uploading} data-testid="upload-folder-btn" className="inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.1em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
                  <FolderPlus className="w-4 h-4" /> Dossier
                </button>
              </div>
              <input ref={fileInput} type="file" multiple hidden onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="file-input" />
              <input ref={folderInput} type="file" multiple hidden {...{ webkitdirectory: "", directory: "" }}
                onChange={(e) => { uploadFolder(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="folder-input" />
            </div>
          )}

          {/* Recherche */}
          {view !== "a_payer" && (
            <div className="mb-6">
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="folder-search"
                placeholder="Rechercher un fichier par nom…"
                className="w-full md:max-w-md bg-white/[0.03] border border-white/10 focus:border-gold outline-none rounded-lg px-4 py-2.5 text-sm transition-colors" />
            </div>
          )}

          {/* Documents Drive */}
          {view !== "a_payer" && (
          <div className="mb-10" data-testid="drive-section">
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold">Documents Drive</p>
              <span className="text-[#5b606e]">·</span>
              <button onClick={() => driveJumpTo(-1)} className="font-mono text-[11px] text-[#A0B2CE] hover:text-gold transition-colors">
                {DOSSIERS.find((d) => d.k === view)?.label}
              </button>
              {drivePath.map((p, i) => (
                <span key={p.id} className="flex items-center gap-2">
                  <ChevronRight className="w-3 h-3 text-[#5b606e]" />
                  <button onClick={() => driveJumpTo(i)} className="font-mono text-[11px] text-[#A0B2CE] hover:text-gold transition-colors max-w-[180px] truncate">{p.name}</button>
                </span>
              ))}
            </div>
            {driveLoading ? (
              <p className="font-mono text-xs tracking-[0.3em] text-gold animate-pulse">CHARGEMENT DRIVE…</p>
            ) : driveItems.length === 0 ? (
              <div className="border border-white/10 rounded-xl p-8 text-center">
                <p className="text-[#A0B2CE] text-sm">Aucun élément dans ce dossier du Drive.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="drive-list">
                {driveItems.filter((it) => it.name.toLowerCase().includes(search.toLowerCase())).map((it) => (
                  it.isFolder ? (
                    <div key={it.id} className="group border border-white/10 rounded-lg p-5 flex items-center gap-4 bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
                      <button onClick={() => openDriveFolder(it)} data-testid={`drive-folder-${it.id}`} className="flex items-center gap-4 min-w-0 flex-1 text-left">
                        <div className="w-11 h-11 rounded-md bg-gold/10 flex items-center justify-center shrink-0"><Folder className="w-5 h-5 text-gold" /></div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate group-hover:text-gold transition-colors">{it.name}</p>
                          <p className="text-[11px] text-[#A0B2CE] font-mono">Dossier</p>
                        </div>
                      </button>
                      {drivePath.length === 0 && (
                        <a href={`${API}/portal/folders/${view}/download-zip?folder_id=${it.id}&auth=${token()}`}
                          data-testid={`download-subfolder-${it.id}`} title="Télécharger ce dossier (.zip)"
                          className="w-9 h-9 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors shrink-0">
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      <ChevronRight className="w-4 h-4 text-[#A0B2CE] group-hover:text-gold shrink-0" />
                    </div>
                  ) : (
                    <button key={it.id} onClick={() => setPreview({ id: it.id, name: it.name, mimeType: it.mimeType, driveUrl: driveFileUrl(it.id) })} data-testid={`drive-file-${it.id}`}
                      className="group border border-white/10 rounded-lg p-5 flex items-center gap-4 bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-left w-full">
                      <div className="w-11 h-11 rounded-md bg-gold/10 flex items-center justify-center shrink-0"><FileIcon className="w-5 h-5 text-gold" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate group-hover:text-gold transition-colors">{it.name}</p>
                        <p className="text-[11px] text-[#A0B2CE] font-mono">{it.size ? humanSize(it.size) : "Document"}</p>
                      </div>
                      <Download className="w-4 h-4 text-[#A0B2CE] group-hover:text-gold shrink-0" />
                    </button>
                  )
                ))}
              </div>
            )}
          </div>
          )}

          {view === "actualite" && (
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold">Déposés dans l'application</p>
              {portalFolder && (
                <>
                  <span className="text-[#5b606e]">·</span>
                  <button onClick={() => setPortalFolder(null)} data-testid="portal-folder-back"
                    className="font-mono text-[11px] text-[#A0B2CE] hover:text-gold transition-colors">
                    Tous les dépôts
                  </button>
                  <ChevronRight className="w-3 h-3 text-[#5b606e]" />
                  <span className="font-mono text-[11px] text-gold flex items-center gap-1.5" data-testid="portal-folder-current">
                    <Folder className="w-3 h-3" /> {portalFolder.name}
                  </span>
                  <a href={`${API}/portal/deposit-folders/${portalFolder.id}/download-zip?auth=${token()}`}
                    data-testid="portal-folder-zip" title="Télécharger tout le dossier (.zip)"
                    className="w-7 h-7 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors">
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </>
              )}
            </div>
          )}
          {view !== "a_payer" && (loading ? (
            <p className="font-mono text-xs tracking-[0.3em] text-gold animate-pulse">CHARGEMENT…</p>
          ) : (() => {
            const visible = (portalFolder
              ? files.filter((f) => !f.is_folder && f.folder_id === portalFolder.id)
              : files.filter((f) => f.is_folder || !f.folder_id)
            ).filter((f) => f.original_filename.toLowerCase().includes(search.toLowerCase()));
            if (visible.length === 0) {
              return view === "actualite" ? (
                <div className="border border-white/10 rounded-xl p-14 text-center" data-testid="empty-state">
                  <p className="text-[#A0B2CE]">{portalFolder ? "Ce dossier est vide." : "Aucun document déposé dans l'application pour ce dossier."}</p>
                </div>
              ) : null;
            }
            return (
            <div className="grid md:grid-cols-2 gap-4" data-testid="files-list">
              {visible.map((f) => (
                <div key={f.id} className="group border border-white/10 rounded-lg p-5 flex items-center gap-4 bg-white/[0.03] hover:bg-white/[0.06] transition-colors" data-testid={`file-${f.id}`}>
                  {f.is_folder ? (
                    <button onClick={() => setPortalFolder({ id: f.id, name: f.original_filename })} data-testid={`open-folder-${f.id}`}
                      className="w-11 h-11 rounded-md bg-gold/10 flex items-center justify-center shrink-0 hover:bg-gold/25 transition-colors" title="Ouvrir le dossier">
                      <Folder className="w-5 h-5 text-gold" />
                    </button>
                  ) : (
                    <FileThumb file={f} onClick={() => setPreview(f)} />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate cursor-pointer hover:text-gold transition-colors"
                      onClick={() => f.is_folder ? setPortalFolder({ id: f.id, name: f.original_filename }) : setPreview(f)}>{f.original_filename}</p>
                    <p className="text-[11px] text-[#A0B2CE] font-mono">
                      {f.is_folder
                        ? `Dossier · ${files.filter((x) => x.folder_id === f.id).length} fichier(s)`
                        : humanSize(f.size)} · {new Date(f.created_at).toLocaleDateString("fr-FR")}
                    </p>
                    {(f.uploaded_by_name || f.uploaded_by) && <p className="text-[11px] text-gold/70 font-mono truncate">Déposé par {f.uploaded_by_name || f.uploaded_by}</p>}
                  </div>
                  {f.is_folder && (
                    <a href={`${API}/portal/deposit-folders/${f.id}/download-zip?auth=${token()}`}
                      data-testid={`zip-folder-${f.id}`} title="Télécharger tout le dossier (.zip)"
                      className="w-9 h-9 rounded-md flex items-center justify-center text-[#A0B2CE] hover:text-gold hover:bg-white/5 transition-colors shrink-0">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  {!f.is_folder && (
                    <a href={`${API}/portal/files/${f.id}/download?auth=${token()}`} target="_blank" rel="noopener noreferrer" title="Télécharger" data-testid={`download-${f.id}`} className="w-9 h-9 rounded-md flex items-center justify-center text-[#A0B2CE] hover:text-gold hover:bg-white/5 transition-colors shrink-0">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  {view === "actualite" && (
                    <button onClick={() => removeFile(f.id)} title="Supprimer" data-testid={`delete-${f.id}`} className="w-9 h-9 rounded-md flex items-center justify-center text-[#A0B2CE] hover:text-red-400 hover:bg-white/5 transition-colors shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            );
          })())}
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-center">
        <Link to="/mentions-legales" data-testid="client-legal-link" className="font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors">
          Mentions légales & confidentialité
        </Link>
      </footer>

      {/* Fiches modal */}
      <AnimatePresence>
        {fiches !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setFiches(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#0B2A52] border border-white/10 rounded-xl p-8" data-testid="fiches-modal">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-gold" />
                  <h2 className="font-serif text-2xl">Vos données personnelles</h2>
                </div>
                <button onClick={() => setFiches(null)}><X className="w-5 h-5 text-[#A0B2CE]" /></button>
              </div>
              {fiches.length === 0 ? (
                <p className="text-[#A0B2CE]">Aucune fiche disponible pour le moment.</p>
              ) : (
                <div className="space-y-3">
                  {fiches.map((f) => (
                    <div key={f.id} data-testid={`fiche-${f.id}`} className="flex items-center gap-4 border border-white/10 rounded-lg p-4 hover:bg-white/5 transition-colors">
                      <FileThumb file={f} onClick={() => setPreview(f)} className="w-9 h-9" />
                      <span className="flex-1 truncate cursor-pointer hover:text-gold transition-colors" onClick={() => setPreview(f)}>{f.original_filename}</span>
                      <a href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" title="Télécharger" className="text-[#A0B2CE] hover:text-gold"><Download className="w-4 h-4" /></a>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <FilePreviewModal file={preview} onClose={() => setPreview(null)} />
      {showEstimator && <CostEstimator onClose={() => setShowEstimator(false)} />}
      {showCession && <CessionSimulator onClose={() => setShowCession(false)} defaultName={user?.name || user?.code || ""} />}
    </div>
  );
}
